"use client"

import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState, useContext } from "react"
import axios from "axios" // 👈 Importante adicionar o axios para buscar as taxas
import { motion, AnimatePresence } from "framer-motion"
import { QrCode, CreditCard, Barcode, ShieldCheck, CheckCircle2 } from "lucide-react"

import CheckoutPixAsaasCircuito from "../../components/CheckoutPixAsaasCircuito"
import CheckoutBoletoAsaasCircuito from "../../components/CheckoutBoletoAsaasCircuito"
import CartaoManual from "../../components/CartaoManualCircuito"
import { DomainContext } from "./LayoutCircuito"
import "../../styles/circuitoDesignSystem.css"

export default function CircuitoInscricaoPagamento() {
  const location = useLocation()
  const navigate = useNavigate()
  const { basePath } = useContext(DomainContext)

  const [metodo, setMetodo] = useState("")
  
  // 💡 ESTADOS PARA GERENCIAMENTO DE TAXAS
  const [taxasConfig, setTaxasConfig] = useState([])
  const [valorTaxa, setValorTaxa] = useState(0)
  const [totalComTaxa, setTotalComTaxa] = useState(0)

  const {
    pedidoId,
    total,
    nomeEvento,
    categoriaSelecionada,
  } = location.state || {}

  useEffect(() => {
    if (!pedidoId) {
      navigate(`${basePath}/inscrever-se`)
    } else {
      setTotalComTaxa(Number(total || 0)) // Define o total inicial
    }
  }, [pedidoId, basePath, navigate, total])

  // 💡 BUSCAR TAXAS CADASTRADAS NO BANCO DE DADOS
  useEffect(() => {
    const fetchTaxas = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api";
        const response = await axios.get(`${apiUrl}/taxas`);
        setTaxasConfig(response.data);
      } catch (error) {
        console.error("Erro ao buscar taxas de serviço:", error);
      }
    };
    fetchTaxas();
  }, []);

  // 💡 CALCULAR TAXA SEMPRE QUE O MÉTODO MUDAR
  useEffect(() => {
    const subtotal = Number(total || 0);

    if (!metodo || taxasConfig.length === 0) {
      setValorTaxa(0);
      setTotalComTaxa(subtotal);
      return;
    }

    // O método selecionado aqui é "cartao", mas no banco as taxas estão como "cartao_credito"
    const metodoBanco = metodo === "cartao" ? "cartao_credito" : metodo;
    const taxaEncontrada = taxasConfig.find(t => t.metodo === metodoBanco);

    if (taxaEncontrada) {
      let taxaCalculada = 0;
      if (taxaEncontrada.tipo === "porcentagem") {
        taxaCalculada = subtotal * (taxaEncontrada.valor / 100);
      } else {
        taxaCalculada = Number(taxaEncontrada.valor); // Valor fixo
      }
      setValorTaxa(taxaCalculada);
      setTotalComTaxa(subtotal + taxaCalculada);
    } else {
      setValorTaxa(0);
      setTotalComTaxa(subtotal);
    }
  }, [metodo, taxasConfig, total]);

  const CardMetodo = ({ id, titulo, icone: Icone }) => {
    const isAtivo = metodo === id
    const [isHovered, setIsHovered] = useState(false)
    
    return (
      <motion.div
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileHover={{ y: -4, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setMetodo(id)}
        className="metodo-card"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "10px", padding: "16px 12px", borderRadius: "16px", cursor: "pointer",
          background: isAtivo ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
          border: isAtivo ? "2px solid var(--c-accent)" : isHovered ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(255,255,255,0.06)",
          boxShadow: isAtivo ? "0 8px 24px rgba(0,0,0,0.3)" : "none", transition: "all 0.2s ease",
          position: "relative", overflow: "hidden", width: "100%"
        }}
      >
        {(isAtivo || isHovered) && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: `radial-gradient(circle at center, ${isAtivo ? 'var(--c-accent)' : 'rgba(255,255,255,0.5)'} 0%, transparent 70%)`, opacity: isAtivo ? 0.1 : 0.03, pointerEvents: "none" }} />
        )}
        <Icone className="metodo-icon" size={32} color={isAtivo || isHovered ? "var(--c-accent)" : "rgba(255,255,255,0.4)"} strokeWidth={isAtivo || isHovered ? 2 : 1.5} style={{ transition: "all 0.3s ease" }} />
        <span className="metodo-text" style={{ fontSize: "13px", fontWeight: isAtivo || isHovered ? "800" : "600", color: isAtivo || isHovered ? "#fff" : "rgba(255,255,255,0.5)", letterSpacing: "0.5px", transition: "all 0.3s ease" }}>{titulo}</span>
        {isAtivo && (<motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ position: "absolute", top: 8, right: 8 }}><CheckCircle2 size={16} color="var(--c-accent)" /></motion.div>)}
      </motion.div>
    )
  }

  return (
    <div className="circuito-shell">
      <style>
        {`
          .grid-metodos-pagamento { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
          @media (max-width: 480px) {
            .grid-metodos-pagamento { gap: 8px; }
            .metodo-card { padding: 12px 8px !important; gap: 8px !important; }
            .metodo-icon { width: 26px !important; height: 26px !important; }
            .metodo-text { font-size: 11px !important; }
            .resumo-valor { font-size: 24px !important; }
          }
        `}
      </style>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ maxWidth: 500, margin: "0 auto", padding: "40px 16px" }}>
        <div style={{ background: "var(--c-card)", borderRadius: "var(--card-radius)", padding: "32px 24px", border: "var(--card-border)", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h1 style={{ fontSize: "24px", fontWeight: 800, color: "#fff", marginBottom: 6 }}>
              Pagamento da <span style={{ color: "var(--c-accent)" }}>Inscrição</span>
            </h1>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", margin: 0 }}>Escolha como deseja finalizar sua associação.</p>
          </div>

          <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.08)", marginBottom: "24px" }}>
            <div style={{ padding: "20px", borderBottom: "1px dashed rgba(255,255,255,0.1)" }}>
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Resumo do Pedido</div>
              <strong style={{ fontSize: "16px", color: "#fff", display: "block", marginBottom: "4px" }}>{nomeEvento}</strong>
              <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}>Categoria: <strong style={{ color: "var(--c-accent)" }}>{categoriaSelecionada}</strong></div>
            </div>
            
            {/* 💡 ÁREA DE RESUMO FINANCEIRO ATUALIZADA COM A TAXA */}
            <div style={{ padding: "16px 20px", background: "rgba(0,0,0,0.2)", borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>Valor da Inscrição</span>
                <span style={{ fontSize: "14px", color: "#fff", fontWeight: 600 }}>R$ {Number(total || 0).toFixed(2)}</span>
              </div>

              <AnimatePresence>
                {metodo && valorTaxa > 0 && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: "auto" }} 
                    exit={{ opacity: 0, height: 0 }} 
                    style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", overflow: "hidden" }}
                  >
                    <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>Taxa de Serviço</span>
                    <span style={{ fontSize: "14px", color: "var(--c-accent)", fontWeight: 600 }}>+ R$ {valorTaxa.toFixed(2)}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>Total a pagar</span>
                <strong className="resumo-valor" style={{ fontSize: "28px", color: "var(--c-accent)", fontWeight: 800, lineHeight: 1 }}>R$ {totalComTaxa.toFixed(2)}</strong>
              </div>
            </div>

          </div>

          <h3 style={{ fontSize: "13px", marginBottom: "12px", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "1px" }}>Selecione o método</h3>
          
          <div className="grid-metodos-pagamento">
            <CardMetodo id="pix" titulo="PIX" icone={QrCode} />
            <CardMetodo id="cartao" titulo="CARTÃO" icone={CreditCard} />
            <CardMetodo id="boleto" titulo="BOLETO" icone={Barcode} />
          </div>

          <AnimatePresence mode="wait">
            {!metodo ? (
              <motion.div key="vazio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ textAlign: "center", padding: "24px", color: "rgba(255,255,255,0.4)" }}>
                <ShieldCheck size={40} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
                <p style={{ fontSize: "14px", margin: 0 }}>Ambiente 100% seguro e criptografado.</p>
              </motion.div>
            ) : (
              <motion.div key={metodo} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} style={{ overflow: "hidden" }}>
                {metodo === "pix" && <CheckoutPixAsaasCircuito pedidoId={pedidoId} valor={totalComTaxa} basePath={basePath} />}
                {metodo === "boleto" && <CheckoutBoletoAsaasCircuito pedidoId={pedidoId} valor={totalComTaxa} />}
                {metodo === "cartao" && <CartaoManual pedidoId={pedidoId} valor={totalComTaxa} nomeEvento={nomeEvento} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div style={{ marginTop: 24, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
          <ShieldCheck size={14} /><span>Protegido por SSL. Seus dados estão seguros.</span>
        </div>
      </motion.div>
    </div>
  )
}